import { criterion, fromCriteria } from "@/core/check";
import type { CheckResult, Lesson } from "@/core/types";
import { type DruckDetails, readDruckDetails } from "@/office/excel/analyse";
import { nichtPruefbar } from "../schnittstelle";

async function mitDruckdetails(bewerten: (details: DruckDetails[]) => CheckResult): Promise<CheckResult> {
  const details = await readDruckDetails();
  if (details === null) {
    return nichtPruefbar(
      "Kopf- und Fußzeilen sowie Seitenumbrüche",
      "1.9",
      "Prüfe in der Druckvorschau (Strg + P), ob Kopf- und Fußzeile erscheinen.",
      true,
    );
  }
  return bewerten(details);
}

export const lektionKopfFusszeile: Lesson = {
  id: "kopf-fusszeile",
  title: "Kopf- und Fußzeile, Seitenumbrüche",
  summary: "Seitenzahl, Dateiname und Stand auf jedem Blatt Papier – und Umbrüche an der richtigen Stelle.",
  durationMinutes: 9,
  learningGoals: [
    "Kopf- und Fußzeilen mit Feldern wie Seitenzahl und Dateiname anlegen",
    "Manuelle Seitenumbrüche in der Umbruchvorschau setzen und verschieben",
  ],
  steps: [
    {
      id: "kopf-und-fusszeile",
      title: "Kopf- und Fußzeile",
      explanation: [
        "Anders als in Word sieht man Kopf- und Fußzeilen in Excel nur in der " +
          "Seitenlayoutansicht und beim Drucken. „Einfügen“ → „Kopf- und Fußzeile“ wechselt " +
          "in diese Ansicht und zeigt drei Felder je Zeile: links, Mitte, rechts.",
        "Die Registerkarte „Kopf- und Fußzeile“ bietet Felder statt getippter Angaben: " +
          "Seitenzahl, Anzahl der Seiten, aktuelles Datum, Dateiname, Blattname. Im Feld " +
          "erscheinen sie als Code, etwa „Seite &[Seite] von &[Seiten]“ – ausgedruckt steht " +
          "dort die richtige Zahl.",
        "Eine bewährte Aufteilung: links der Dateiname, Mitte der Blattname, rechts „Seite " +
          "X von Y“. Wer einen Ausdruck in der Hand hält, weiß dann immer, woher er stammt " +
          "und ob eine Seite fehlt.",
      ],
      keyPoints: [
        "Einfügen → Kopf- und Fußzeile.",
        "Felder statt getippter Werte: Seite, Seiten, Datei, Blatt.",
        "Seite X von Y verrät, ob eine Seite fehlt.",
      ],
      task: {
        instruction:
          "Richte für ein Blatt eine Fußzeile mit „Seite X von Y“ und eine Kopfzeile mit dem Datei- oder Blattnamen ein.",
        hint: "Einfügen → Kopf- und Fußzeile, in die Fußzeile wechseln, „Seitenzahl“ und „Anzahl der Seiten“ einfügen.",
        check: () =>
          mitDruckdetails((details) => {
            const mitSeiten = details.filter((blatt) => /&P/.test(blatt.fusszeile) && /&N/.test(blatt.fusszeile));
            const mitName = details.filter((blatt) => /&F|&A/.test(blatt.kopfzeile));
            return fromCriteria(
              [
                criterion("Eine Fußzeile zeigt „Seite X von Y“", mitSeiten.length > 0),
                criterion("Eine Kopfzeile zeigt Datei- oder Blattnamen", mitName.length > 0),
              ],
              {
                success: "Jede ausgedruckte Seite sagt jetzt, woher sie stammt und ob eine fehlt.",
                failure: "Kopf- oder Fußzeile sind noch nicht vollständig.",
                hint: "Getippte Seitenzahlen zählen nicht – die Felder stehen auf der Registerkarte „Kopf- und Fußzeile“.",
              },
            );
          }),
      },
    },
    {
      id: "seitenumbruch",
      title: "Seitenumbrüche setzen",
      explanation: [
        "In der Umbruchvorschau („Ansicht“ → „Umbruchvorschau“) zeigt Excel die " +
          "automatischen Umbrüche als gestrichelte blaue Linien. Eine Linie lässt sich mit der " +
          "Maus verschieben – aus der gestrichelten wird dann eine durchgezogene, ein " +
          "manueller Umbruch.",
        "Gezielt geht es über „Seitenlayout“ → „Umbrüche“ → „Seitenumbruch einfügen“: Der " +
          "Umbruch entsteht oberhalb der markierten Zeile. So beginnt etwa jede Region auf " +
          "einer neuen Seite. „Alle Seitenumbrüche zurücksetzen“ macht alles rückgängig.",
      ],
      keyPoints: [
        "Ansicht → Umbruchvorschau: Linien ziehen.",
        "Seitenlayout → Umbrüche → Seitenumbruch einfügen (oberhalb der Markierung).",
      ],
      task: {
        instruction: "Setze in einer längeren Liste einen manuellen Seitenumbruch, etwa vor einer neuen Gruppe.",
        hint: "Erste Zeile der neuen Gruppe markieren, Seitenlayout → Umbrüche → Seitenumbruch einfügen.",
        check: () =>
          mitDruckdetails((details) => {
            const mitUmbruch = details.filter((blatt) => blatt.umbrueche > 0);
            return fromCriteria(
              [
                criterion(
                  mitUmbruch[0] ? `„${mitUmbruch[0].sheetName}“: ${mitUmbruch[0].umbrueche} manuelle Umbrüche` : "Ein manueller Seitenumbruch",
                  mitUmbruch.length > 0,
                ),
              ],
              {
                success: "Die neue Gruppe beginnt auf einer neuen Seite.",
                failure: "Es gibt noch keinen manuellen Seitenumbruch.",
              },
            );
          }),
      },
    },
  ],
};
